import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  InventoryTransactionsParams,
  InventoryTransactionsResponse,
  InventoryTransactionsResult,
} from '../types/inventoryTransaction';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getInventoryTransactions = async (
  params?: InventoryTransactionsParams
): Promise<InventoryTransactionsResult> => {
  const queryParams = new URLSearchParams();

  if (params?.type) {
    queryParams.set('type', params.type);
  }
  if (params?.item_id) {
    queryParams.set('item_id', String(params.item_id));
  }
  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.search) {
    queryParams.set('search', params.search);
  }
  if (params?.recent_days !== undefined) {
    queryParams.set('recent_days', String(params.recent_days));
  }
  if (params?.include_running_balance !== undefined) {
    queryParams.set('include_running_balance', params.include_running_balance ? '1' : '0');
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_TRANSACTIONS.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch inventory transactions');
  }

  const result: InventoryTransactionsResponse = await response.json();
  return {
    summary: result.summary,
    movements: result.data,
    meta: result.meta,
    links: result.links,
  };
};
